// ModalityApp.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './index.css'; // Make sure this file is updated with the new keyframes
import SlopeLogoGreen from './Slope Middle Edit enlarged green.svg';
import SupportDrawer from './components/SupportDrawer';
import LiveChatRail from './components/LiveChatRail';
import TicketsDrawer from './components/TicketsDrawer';
import BackgroundLayer from './components/BackgroundLayer';
import TopBar from './components/TopBar';
import { DocumentNotifications, TicketUpdateBanner } from './components/DocumentNotifications';
import LinkTreeButton from './components/LinkTreeButton';
import LoginGates from './components/LoginGates';
import ConfirmModal from './components/ConfirmModal';
import DocumentViewer from './components/viewer/DocumentViewer';
import MainPanel from './components/main/MainPanel';
import { requestNotificationPermission } from './utils/notify';
import { authFetch } from './utils/authFetch';
import { DISPLAY_DEFAULTS } from './utils/displayDefaults';
import { isCategoryTaxable } from './utils/categories';
import { gradientPresets, ANIMATION_STYLES, PATTERN_STYLES } from './utils/displayPresets';
import { useDisplaySettings } from './hooks/useDisplaySettings';
import { useAuth } from './hooks/useAuth';
import { usePdfViewer } from './hooks/usePdfViewer';
import { useFdLiveChat } from './hooks/useFdLiveChat';
import { useSupport } from './hooks/useSupport';
import { useAppData } from './hooks/useAppData';
import { getStatusBadgeClass, getStatusOutlineClass, getStatusEmoji } from './utils/statusHelpers';
import { ADDON_REGEX, ADDON_CLEANUP_REGEX } from './utils/addonRegex';


// Firebase client auth is not used for admin Google sign-in right now
const Modals = React.lazy(() => import('./components/Modals'));
const SopModal = React.lazy(() => import('./components/SopModal'));
const AdminPanel = React.lazy(() => import('./components/AdminPanel'));
const InventoryApp = React.lazy(() => import('./components/inventory/InventoryApp'));
const InvoiceApp = React.lazy(() => import('./components/invoices/InvoiceApp'));
const CoverageApp = React.lazy(() => import('./components/coverage/CoverageApp'));


const defaults = {
  categoryColor: '#314e4a',
  DAYS: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  SHIFTS: ["OFF", "AM", "PM", "DOUBLE"],
  linkTreeConfig: {
    links: [
      { id: 1, label: 'Home', url: '#' },
      { id: 2, label: 'Our Services', url: '#' },
      { id: 3, label: 'Our Team', url: '#' },
      { id: 4, label: 'Memberships', url: '#' },
      { id: 5, label: 'Insurance Coverage', url: '#' },
    ],
    size: 240
  },
  titleConfig: {
    text: 'Slope Wellness | Operations',
    color: '#FFFFFF',
    fontSize: '3rem'
  },
  overviewPosition: { x: window.innerWidth - 400, y: 16 },
  isMuted: false,
  ...DISPLAY_DEFAULTS,
};

export default function ModalityApp() {
  const [, setLog] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalForm, setModalForm] = useState({});

  const [mainBgColor, setMainBgColor] = useState('#314e4a');
  const [showAnimationControls, setShowAnimationControls] = useState(false);

  const [, setClickCount] = useState(0);
  const clickTimeoutRef = useRef(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);

  const [saveStatus, setSaveStatus] = useState('');
  const [networkToast, setNetworkToast] = useState('');

  const [isLinkTreeVisible, setIsLinkTreeVisible] = useState(false);
  const [isOverviewCollapsed, setIsOverviewCollapsed] = useState(false);
  const linkTreeRef = useRef(null);

  // Auth (admin + front-desk write scopes) is owned by useAuth. Downstream
  // hooks read adminMode / frontdeskAuth / writeAuthEpoch from here.
  //
  // playClickSound is provided by useDisplaySettings, which runs AFTER
  // useAuth. We sidestep the ordering cycle by passing a stable wrapper
  // that reads from a ref, then populating the ref from displaySettings'
  // playClickSound after every commit. Safe because useAuth's handlers
  // call playClickSound only at event time (post-first-commit), never
  // during render.
  const playClickSoundRef = useRef(() => {});
  const playClickSoundStable = useCallback(() => playClickSoundRef.current(), []);
  const auth = useAuth({
    playClickSound: playClickSoundStable,
    onLogout: () => setShowAnimationControls(false),
  });
  // Skip setAdminMode / setFrontdeskAuth — no external caller, and pulling
  // them into local bindings would trip no-unused-vars.
  const {
    adminMode,
    frontdeskAuth,
    password, setPassword,
    fdPassword, setFdPassword,
    adminLoginError, setAdminLoginError,
    fdLoginError, setFdLoginError,
    loginVisible, setLoginVisible,
    writeAuthEpoch,
    authHydrated,
    inputRef,
    loginTimeoutRef,
    handleLogin,
    handleFrontdeskLogin,
    handleLogout,
    handleSetAdminPassword,
  } = auth;

  // Display / appearance state is owned by useDisplaySettings. The hook
  // also owns the debounced auto-save effect, the overview-panel drag
  // listeners, playClickSound, and the settings-hydrated gate.
  //
  // onAutoSave is routed through a ref so the hook can call the latest
  // `() => handleSave(getAllData())` closure, even though handleSave and
  // getAllData are declared later in this component (refs sidestep the
  // temporal-dead-zone problem).
  const onAutoSaveRef = useRef(() => {});
  const displaySettings = useDisplaySettings({
    adminMode,
    onAutoSave: () => onAutoSaveRef.current(),
  });
  const {
    animationOpacity, setAnimationOpacity,
    animationSpeed, setAnimationSpeed,
    gradientPreset, setGradientPreset,
    customGradientColors, setCustomGradientColors,
    gradientAngle, setGradientAngle,
    animationStyle, setAnimationStyle,
    patternStyle, setPatternStyle,
    patternSpeed, setPatternSpeed,
    patternOpacity, setPatternOpacity,
    disableBackgroundEffects, setDisableBackgroundEffects,
    amShiftBgColor, setAmShiftBgColor,
    pmShiftBgColor, setPmShiftBgColor,
    offShiftBgColor, setOffShiftBgColor,
    doubleShiftBgColor, setDoubleShiftBgColor,
    amShiftTextColor, setAmShiftTextColor,
    pmShiftTextColor, setPmShiftTextColor,
    offShiftTextColor, setOffShiftTextColor,
    doubleShiftTextColor, setDoubleShiftTextColor,
    titleConfig, setTitleConfig,
    linkTreeConfig, setLinkTreeConfig,
    overviewPosition,
    isMuted, setIsMuted,
    serviceInfoFontSizeLevel,
    sopButtonText, setSopButtonText,
    sopButtonLink, setSopButtonLink,
    sopButtonBgColor, setSopButtonBgColor,
    sopButtonTextColor, setSopButtonTextColor,
    overviewRef,
    handleDragMouseDown,
    selectedGradientCss,
    animationClassName,
    patternClassName,
    playClickSound,
    getShiftStyles,
    getServiceInfoFontSizeClass,
    increaseFontSize,
    decreaseFontSize,
    hydrateFromServer: hydrateDisplayFromServer,
    getSlice: getDisplaySlice,
  } = displaySettings;

  // Point useAuth's playClickSound wrapper at useDisplaySettings'
  // playClickSound after every commit. useAuth runs before
  // useDisplaySettings (because useDisplaySettings needs adminMode from
  // useAuth), so it cannot receive playClickSound directly. The ref
  // indirection closes the cycle safely — handlers inside useAuth only
  // fire at event time, long after the first commit populates the ref.
  useEffect(() => {
    playClickSoundRef.current = playClickSound;
  });

  // Document viewer state (pdfViewerUrl/Title/Filename/zoom/finalUrl/
  // errorCount/isPdfFilterEnabled, isSigning, ackName, viewingDocument,
  // activeViewerTab setter, the three hide toggles, desc/pdf split
  // percents, viewerRef) plus handlePrintPdf / handleDownloadPdf /
  // startDrag / closeViewer live in usePdfViewer. Document-mutating
  // handlers (handleAcknowledge, handleSaveSignature,
  // handleDeleteAcknowledgement) still live in the parent because they
  // rewrite `documents` + call handleSave(getAllData()) — they'll move
  // into useAppData in Phase 6f.
  const {
    viewingDocument, setViewingDocument,
    pdfZoomLevel, setPdfZoomLevel,
    pdfFinalUrl,
    pdfErrorCount,
    isPdfFilterEnabled, setIsPdfFilterEnabled,
    isSigning, setIsSigning,
    ackName, setAckName,
    setActiveViewerTab,
    hideDescription, setHideDescription,
    hideDocument, setHideDocument,
    hideVideo, setHideVideo,
    descPercent,
    viewerRef,
    startDrag,
    handlePrintPdf,
    handleDownloadPdf,
    closeViewer,
  } = usePdfViewer();

  // App data (services / practitioners / categories / documents /
  // documentCategories / recentlyViewed / featuredDocumentId /
  // dataLoading / salesTax) + the initial /api/data fetch + the
  // data-mutating handlers that don't touch modal state live in
  // useAppData. Placed after usePdfViewer so we can feed its setters
  // (setViewingDocument / setAckName / setIsSigning + ackName) into
  // the acknowledgement handlers.
  //
  // onSave is routed through a ref so the hook can call the latest
  // `(updatedState) => handleSave({ ...getAllData(), ...updatedState })`
  // closure despite handleSave / getAllData being declared below
  // (same TDZ workaround as useDisplaySettings' onAutoSave).
  const onSaveRef = useRef(async () => {});
  const app = useAppData({
    defaults,
    onHydrated: ({ json, processedCategories }) => {
      // Seed the services-column background color from the first
      // category (or fall back). Selection is always empty on first
      // load since it isn't persisted.
      if (processedCategories.length > 0) setMainBgColor(processedCategories[0].color);
      else setMainBgColor('#314e4a');
      // Hand the raw json to useDisplaySettings so gradient /
      // animation / pattern / shift-color / title-link-tree / sop /
      // isMuted / overviewPosition all seed from the same load.
      hydrateDisplayFromServer(json);
    },
    onSave: (updatedState) => onSaveRef.current(updatedState),
    onLoadError: (err) => setLog((prev) => [...prev, 'Error loading data: ' + err.message]),
    playClickSound,
    ackName,
    setAckName,
    setIsSigning,
    setViewingDocument,
    setShowConfirmModal,
    setSaveStatus,
  });
  const {
    data, setData,
    dataLoading,
    categories, setCategories,
    specialties, setSpecialties,
    documents, setDocuments,
    documentCategories, setDocumentCategories,
    appSalesTaxRate, setAppSalesTaxRate,
    includeSalesTax, setIncludeSalesTax,
    handleRestore,
    handleDeleteDocument,
    handleFeatureDocument, // eslint-disable-line no-unused-vars
    handleAcknowledge, // eslint-disable-line no-unused-vars
    handleSaveSignature,
    handleDeleteAcknowledgement,
    getSlice: getAppDataSlice,
  } = app;

  const [dismissedNotifications, setDismissedNotifications] = useState([]);
  const [sopModalVisible, setSopModalVisible] = useState(false);
  const [sopModalForm, setSopModalForm] = useState({});
  const [sopSearchQuery, setSopSearchQuery] = useState('');
  const [selectedSopCategory, setSelectedSopCategory] = useState([]);
  const [viewingArchived, setViewingArchived] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchWrapperRef = useRef(null);

  const [activePracTab, setActivePracTab] = useState('bio');

  const [collapsedServices, setCollapsedServices] = useState({});
  const [newInsuranceNetwork, setNewInsuranceNetwork] = useState('');
  const [showEasterEggText, setShowEasterEggText] = useState(false);
  const [isHeaderBouncing, setIsHeaderBouncing] = useState(false);
  // pdfViewerUrl/Title/Filename/zoom/finalUrl/errorCount/isPdfFilterEnabled,
  // isSigning, ackName, viewingDocument, activeViewerTab setter all moved
  // into usePdfViewer (destructured above).
  const [supportDrawerOpen, setSupportDrawerOpen] = useState(false);
  const [liveChatOpen, setLiveChatOpen] = useState(false);
  const [ticketsDrawerOpen, setTicketsDrawerOpen] = useState(false);
  // Support surface (Jonathan presence, categories, employees, tickets,
  // read state, unread count, chime/title-flash effects, and four
  // handlers) lives in useSupport. Drawer booleans + setter stay in the
  // parent because other UI surfaces toggle them too. useSupport must
  // run BEFORE useFdLiveChat — the latter consumes `jonathanStatus`.
  const {
    supportCategories,
    setSupportCategories,
    supportEmployees,
    setSupportEmployees,
    fdSupportEmployeesList,
    supportSelectedEmployeeId,
    handleSelectSupportEmployee,
    jonathanStatus,
    jonathanAvailableUntil,
    handleSetJonathanStatus,
    supportMyTickets,
    supportSeenAt,
    supportTicketSeenMap,
    supportUnreadCount,
    handleMarkSupportSeen,
    handleMarkTicketSeen,
  } = useSupport({
    adminMode,
    frontdeskAuth,
    writeAuthEpoch,
    authHydrated,
    supportDrawerOpen,
    ticketsDrawerOpen,
    setTicketsDrawerOpen,
  });
  // Viewer hide toggles, desc/pdf split percents, dragInfoRef,
  // startDrag/handleDragging/stopDrag, and the reset-hide-defaults
  // effect all moved into usePdfViewer.

  const { DAYS, SHIFTS } = defaults;


  const toggleServiceCollapse = (serviceName) => { playClickSound(); setCollapsedServices(prev => ({ ...prev, [serviceName]: !prev[serviceName] })); };



  const apiCall = async (method, endpoint, data = null) => {
    if (window.ipcRenderer) {
      try {
        return await window.ipcRenderer.invoke(endpoint, data);
      } catch (error) {
        console.error('Electron API error:', error);
        setLog(prev => [...prev, `Electron API error: ${error.message}`]);
        throw error;
      }
    } else {
      // Browser environment connected to Express/Firebase
      try {
        if (endpoint.includes('data') && method === 'GET') {
          const response = await fetch('/api/data');
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return await response.json();
        } else if (endpoint.includes('save') && method === 'POST') {
          const response = await authFetch('/api/save', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          });
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return await response.json();
        } else {
          throw new Error(`Unsupported API call: ${method} ${endpoint}`);
        }
      } catch (error) {
        console.error('Web API error:', error);
        setLog(prev => [...prev, `Web API error: ${error.message}`]);
        throw error;
      }
    }
  };
  
  // handleSetAdminPassword moved into useAuth (destructured above).

  // Helper to gather all persistable app data for saving.
  // Data-layer fields (categories/services/practitioners/documents/
  // documentCategories/recentlyViewed/featuredDocumentId/salesTaxRate)
  // come from useAppData via getAppDataSlice(). Display-layer fields
  // (gradient/animation/pattern/titleConfig/linkTreeConfig/sopButton*/
  // overviewPosition/isMuted) come from useDisplaySettings via
  // getDisplaySlice(). Key order mirrors the pre-refactor shape so
  // /api/save receives a byte-identical payload.
  const getAllData = () => {
    const displaySlice = getDisplaySlice();
    const appSlice = getAppDataSlice();
    return {
      categories: appSlice.categories,
      specialties: appSlice.specialties,
      services: appSlice.services,
      practitioners: appSlice.practitioners,
      documents: appSlice.documents,
      documentCategories: appSlice.documentCategories,
      recentlyViewed: appSlice.recentlyViewed,
      featuredDocumentId: appSlice.featuredDocumentId,
      titleConfig: displaySlice.titleConfig,
      linkTreeConfig: displaySlice.linkTreeConfig,
      defaults,
      salesTaxRate: appSlice.salesTaxRate,
      animationOpacity: displaySlice.animationOpacity,
      animationSpeed: displaySlice.animationSpeed,
      gradientPreset: displaySlice.gradientPreset,
      customGradientColors: displaySlice.customGradientColors,
      gradientAngle: displaySlice.gradientAngle,
      animationStyle: displaySlice.animationStyle,
      patternStyle: displaySlice.patternStyle,
      patternSpeed: displaySlice.patternSpeed,
      patternOpacity: displaySlice.patternOpacity,
      isMuted: displaySlice.isMuted,
      sopButtonText: displaySlice.sopButtonText,
      sopButtonLink: displaySlice.sopButtonLink,
      sopButtonBgColor: displaySlice.sopButtonBgColor,
      sopButtonTextColor: displaySlice.sopButtonTextColor,
      overviewPosition: displaySlice.overviewPosition,
    };
  };

  const handleCloseModal = () => {
    playClickSound();
    setModalVisible(false);
    setSopModalVisible(false);
    setModalForm({});
    setSopModalForm({});
  };

  const handleSearchResultClick = (result) => {
    playClickSound();
    if (!result) return;
    if (result.type === 'Service') {
      setSelectedService(result);
      if (result.category) setSelectedCategory(result.category);
    } else if (result.type === 'Practitioner') {
      setSelectedPractitioner(result);
      setActivePracTab('bio');
    }
    setSearchQuery('');
    setSearchResults([]);
    setIsSearchFocused(false);
  };

  // The initial /api/data fetch + record normalization + display-layer
  // hydration cascade (via the `onHydrated` callback above, which calls
  // `hydrateDisplayFromServer(json)` + seeds `mainBgColor` from the
  // first processed category) all live in useAppData.

  // Support-surface effects / memos / handlers (categories + presence
  // + employees + read-state hydration + tickets poll + unread-count
  // memo + chime / title-flash effects + the four handlers) all live
  // in useSupport above. The 401 recovery listener moved into useAuth.

  useEffect(() => {
    const onTimeout = () => {
      setNetworkToast('Request timed out — check your connection and try again.');
      setTimeout(() => setNetworkToast(''), 6000);
    };
    window.addEventListener('modality:fetch-timeout', onTimeout);
    return () => window.removeEventListener('modality:fetch-timeout', onTimeout);
  }, []);

  // FD ↔ Jonathan live chat (session/messages/loading/unread, rail
  // open state, polling pipeline, chime + notification, Escape to
  // close) lives in useFdLiveChat. `jonathanStatus` + the two
  // drawer/tab booleans are the hook's only inputs; everything else
  // is owned internally.
  const {
    fdLiveChatSession,
    fdLiveChatHasUnread,
    liveChatRailOpen, setLiveChatRailOpen,
    fdLiveChatShared,
  } = useFdLiveChat({ jonathanStatus, supportDrawerOpen, liveChatOpen });

  // The FD-auth restore useLayoutEffect and the loginVisible
  // auto-collapse timer both moved into useAuth.

  useEffect(() => {
    setActivePracTab('bio');
  }, [selectedPractitioner]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isLinkTreeVisible && linkTreeRef.current && !linkTreeRef.current.contains(event.target) && !event.target.closest('#logo-button')) {
        setIsLinkTreeVisible(false);
      }
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLinkTreeVisible]);

  useEffect(() => {
    if (searchQuery.length > 1) {
      const lowerCaseQuery = searchQuery.toLowerCase();
      const filteredServices = data.services
        .filter(s => !s.isArchived && s.name.toLowerCase().includes(lowerCaseQuery))
        .map(s => ({ ...s, type: 'Service' }));

      const filteredPractitioners = data.practitioners
        .filter(p => !p.isArchived && p.name.toLowerCase().includes(lowerCaseQuery))
        .map(p => ({ ...p, type: 'Practitioner' }));

      setSearchResults([...filteredServices, ...filteredPractitioners]);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, data.services, data.practitioners]);


  // handleLogin / handleFrontdeskLogin / handleLogout and the
  // block-interactions global listener all moved into useAuth.

  const handleSave = async (dataToPersist) => {
    try {
          await apiCall('POST', 'save', dataToPersist);
      setSaveStatus('Saved successfully!');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Save error:', error);
      setSaveStatus(`Save failed: ${error.message}`);
      setTimeout(() => setSaveStatus(''), 5000);
    }
  };

              
  const handleModalSave = async () => {
    playClickSound();
    let updatedState = {};

    if (modalType === 'document') {
      // prepare file upload if a new file was selected
      let fileUrl = sopModalForm.fileUrl || '';
      let filePath = sopModalForm.filename || sopModalForm.fileName || '';

      if (sopModalForm.file) {
        // Use a signed upload URL (Vercel serverless friendly): request signed URL, then PUT file directly to GCS
        const originalName = sopModalForm.file.name || (filePath.split('/').pop() || 'upload');
        const newFileName = `${Date.now()}-${originalName}`;
        try {
          const resp = await authFetch('/api/get-upload-url', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ filename: newFileName, contentType: sopModalForm.file.type })
          });
          const urlResult = await resp.json();
          if (!resp.ok) throw new Error(urlResult.error || 'Failed to get upload URL');

          // Upload file directly to storage using the signed URL
          const putResp = await fetch(urlResult.uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': sopModalForm.file.type || 'application/octet-stream' },
            body: sopModalForm.file
          });
          if (!putResp.ok) {
            const text = await putResp.text();
            throw new Error('Upload failed: ' + text);
          }

          fileUrl = urlResult.publicUrl;
          filePath = originalName;
          sopModalForm._uploadedContentType = sopModalForm.file.type || '';
        } catch (error) {
          console.error('Upload error:', error);
          setSaveStatus(`Upload failed: ${error.message}`);
          setTimeout(() => setSaveStatus(''), 5000);
          return;
        }
      }

      const newDocument = {
        id: sopModalForm.isEdit ? sopModalForm.id : Date.now().toString(),
        name: sopModalForm.name,
        description: sopModalForm.description,
        categoryIds: sopModalForm.categoryIds || [],
        supportCategoryIds: Array.isArray(sopModalForm.supportCategoryIds) ? sopModalForm.supportCategoryIds : [],
        // single status selection (keep `statuses` array with one element for compatibility)
        statuses: sopModalForm.status ? [sopModalForm.status] : [],
        status: sopModalForm.status || '',
        filename: filePath,
        fileUrl: fileUrl,
        videoUrl: sopModalForm.videoUrl || '',
        // fileType helps render uploaded files (e.g., 'video/mp4') or mark external embeds
        fileType: sopModalForm._uploadedContentType || (sopModalForm.file ? sopModalForm.file.type : (sopModalForm.videoUrl ? 'embed' : '')),
        isArchived: sopModalForm.status === 'Expired',
        signatureRequired: !!sopModalForm.signatureRequired,
        displayStatusNotification: sopModalForm.displayStatusNotification !== false,
        acknowledgements: sopModalForm.isEdit ? sopModalForm.acknowledgements : [],
      };

      const newDocuments = sopModalForm.isEdit
        ? documents.map((doc) => (doc.id === newDocument.id ? newDocument : doc))
        : [...documents, newDocument];

      setDocuments(newDocuments);
      updatedState = { documents: newDocuments };
    } else if (modalType === 'documentCategory') {
      updatedState = { documentCategories };
    } else if (modalType === 'category') {
      let finalCategories;
      let servicesPayload = data.services;
      const categoryEntry = { name: modalForm.name, color: modalForm.color || defaults.categoryColor || '#314e4a', taxable: modalForm.taxable !== false };
      if (modalForm.isEdit) {
        finalCategories = categories.map(cat => (cat.name === modalForm.originalName ? categoryEntry : cat));
        servicesPayload = data.services.map(s => s.category === modalForm.originalName ? { ...s, category: modalForm.name } : s);
      } else {
        if (!categories.some(cat => cat.name === modalForm.name)) { finalCategories = [...categories, categoryEntry]; } else { setLog(prev => [...prev, 'Category already exists: ' + modalForm.name]); setModalVisible(false); return; }
      }
      setCategories(finalCategories);
      setData(d => ({ ...d, services: servicesPayload }));
      updatedState = { categories: finalCategories, services: servicesPayload };
    } else if (modalType === 'specialty') {
      const name = (modalForm.name || '').trim();
      if (!name) { setLog(prev => [...prev, 'Specialty name is required']); return; }
      const entry = { name, category: modalForm.category || '', color: modalForm.color || defaults.categoryColor || '#314e4a' };
      let finalSpecialties;
      let practitionersPayload = data.practitioners;
      if (modalForm.isEdit) {
        if (name !== modalForm.originalName && specialties.some(sp => sp.name === name)) { setLog(prev => [...prev, 'Specialty already exists: ' + name]); setModalVisible(false); return; }
        finalSpecialties = specialties.map(sp => (sp.name === modalForm.originalName ? entry : sp));
        // Rename cascade: practitioners store the specialty by name.
        if (name !== modalForm.originalName) {
          practitionersPayload = data.practitioners.map(p => p.specialty === modalForm.originalName ? { ...p, specialty: name } : p);
        }
      } else {
        if (specialties.some(sp => sp.name === name)) { setLog(prev => [...prev, 'Specialty already exists: ' + name]); setModalVisible(false); return; }
        finalSpecialties = [...specialties, entry];
      }
      setSpecialties(finalSpecialties);
      setData(d => ({ ...d, practitioners: practitionersPayload }));
      updatedState = { specialties: finalSpecialties, practitioners: practitionersPayload };
    } else if (modalType === 'service') {
      const updatedServices = modalForm.isEdit ? data.services.map(s => (s.name === modalForm.originalName ? { ...s, ...modalForm } : s)) : [...data.services, { ...modalForm, description: modalForm.description || '', isArchived: false }];
      const serviceNameChanged = modalForm.isEdit && modalForm.name !== modalForm.originalName;
      // Durations a practitioner can still offer for this service. Editing the
      // service's duration/price list must drop practitioner durations that
      // no longer exist, otherwise they keep showing a stale timing.
      const validDurations = new Set((modalForm.durationPrices || []).map(dp => Number(dp.duration)).filter(d => d > 0));
      let practitionersChanged = serviceNameChanged;
      const updatedPractitionersForService = modalForm.isEdit
        ? data.practitioners.map(p => ({
            ...p,
            offered_services: (p.offered_services || []).map(os => {
              if (os.serviceName !== modalForm.originalName) return os;
              const prunedDurations = (os.offeredDurations || []).filter(d => validDurations.has(Number(d)));
              if (prunedDurations.length !== (os.offeredDurations || []).length) practitionersChanged = true;
              return { ...os, serviceName: modalForm.name, offeredDurations: prunedDurations };
            }),
          }))
        : data.practitioners;
      setData(d => ({ ...d, services: updatedServices, practitioners: updatedPractitionersForService }));
      updatedState = practitionersChanged
        ? { services: updatedServices, practitioners: updatedPractitionersForService }
        : { services: updatedServices };
    } else if (modalType === 'practitioner') {
      const updatedPractitioners = modalForm.isEdit ? data.practitioners.map(p => (p.name === modalForm.originalName ? { ...p, ...modalForm } : p)) : [...data.practitioners, { ...modalForm, offered_services: modalForm.offered_services || [], insuranceNetworks: modalForm.insuranceNetworks || [], bedHeight: modalForm.bedHeight || 3, isArchived: false }];
      setData(d => ({ ...d, practitioners: updatedPractitioners }));
      updatedState = { practitioners: updatedPractitioners };
    } else if (modalType === 'linkTree') {
      setLinkTreeConfig(modalForm.config);
      updatedState = { linkTreeConfig: modalForm.config };
    } else if (modalType === 'titleConfig') {
      setTitleConfig(modalForm.config);
      updatedState = { titleConfig: modalForm.config };
    } else { return; }

    await handleSave({ ...getAllData(), ...updatedState });
    setModalVisible(false);
    setNewInsuranceNetwork('');
  };

  const handleArchive = async () => {
    playClickSound();
    let updatedState = {};

    if (modalType === 'service') {
      const serviceToArchive = modalForm.originalName;
      const updatedServices = data.services.map(s => s.name === serviceToArchive ? { ...s, isArchived: true } : s);
      setData(d => ({ ...d, services: updatedServices }));
      if (selectedService && selectedService.name === serviceToArchive) setSelectedService(null);
      updatedState = { services: updatedServices };

    } else if (modalType === 'practitioner') {
      const practitionerToArchive = modalForm.originalName;
      const updatedPractitioners = data.practitioners.map(p => p.name === practitionerToArchive ? { ...p, isArchived: true } : p);
      setData(d => ({ ...d, practitioners: updatedPractitioners }));
      if (selectedPractitioner && selectedPractitioner.name === practitionerToArchive) setSelectedPractitioner(null);
      updatedState = { practitioners: updatedPractitioners };
    } else {
      return;
    }

    await handleSave({ ...getAllData(), ...updatedState });
    setModalVisible(false);
    setShowConfirmModal(false);
  };

  // handleRestore moved into useAppData.

  const handleDelete = async () => {
    playClickSound();
    let updatedState = {};

    if (modalType === 'category') {
      const categoryToDelete = modalForm.originalName;
      const updatedCategories = categories.filter(cat => cat.name !== categoryToDelete);
      const updatedServices = data.services.map(s => s.category === categoryToDelete ? { ...s, category: '' } : s);
      setCategories(updatedCategories);
      setData({ services: updatedServices, practitioners: data.practitioners });
      if (selectedCategory === categoryToDelete) { setSelectedCategory(''); setMainBgColor(defaults.categoryColor || '#314e4a'); }
      updatedState = { categories: updatedCategories, services: updatedServices };
    } else if (modalType === 'specialty') {
      const specialtyToDelete = modalForm.originalName;
      const updatedSpecialties = specialties.filter(sp => sp.name !== specialtyToDelete);
      // Mirror category delete: practitioners on this specialty are left unassigned.
      const updatedPractitioners = data.practitioners.map(p => p.specialty === specialtyToDelete ? { ...p, specialty: '' } : p);
      setSpecialties(updatedSpecialties);
      setData(d => ({ ...d, practitioners: updatedPractitioners }));
      updatedState = { specialties: updatedSpecialties, practitioners: updatedPractitioners };
    } else if (modalType === 'service') {
      const serviceToDelete = modalForm.originalName;
      const updatedServices = data.services.filter(s => s.name !== serviceToDelete);
      const updatedPractitioners = data.practitioners.map(p => ({ ...p, offered_services: p.offered_services.filter(os => os.serviceName !== serviceToDelete) }));
      setData({ services: updatedServices, practitioners: updatedPractitioners });
      if (selectedService && selectedService.name === serviceToDelete) setSelectedService(null);
      updatedState = { services: updatedServices, practitioners: updatedPractitioners };
    } else if (modalType === 'practitioner') {
      const practitionerToDelete = modalForm.originalName;
      const updatedPractitioners = data.practitioners.filter(p => p.name !== practitionerToDelete);
      setData(d => ({ ...d, practitioners: updatedPractitioners }));
      if (selectedPractitioner && selectedPractitioner.name === practitionerToDelete) setSelectedPractitioner(null);
      updatedState = { practitioners: updatedPractitioners };
    } else { return; }

    await handleSave({ ...getAllData(), ...updatedState });
    setModalVisible(false);
    setShowConfirmModal(false);
  };

  // handleDeleteDocument + handleFeatureDocument moved into useAppData.

  const handleSaveAllSettings = () => {
    playClickSound();
    handleSave(getAllData());
  };

  // Point the hook's auto-save callback at the latest handleSave+getAllData
  // closure after every commit. The hook's debounced effect reads .current
  // inside its setTimeout, so it always invokes the freshest closure —
  // critical because handleSave/getAllData are declared after useDisplaySettings
  // runs (TDZ workaround via ref). Using useEffect with no deps (runs every
  // commit) keeps the render body pure for Strict Mode's double-invocation.
  useEffect(() => {
    onAutoSaveRef.current = () => handleSave(getAllData());
    // useAppData's data-mutating handlers call `onSave(updatedState)`
    // which the parent expands into the full payload with the fresh
    // slice override. Same ref-trick rationale as onAutoSaveRef above.
    onSaveRef.current = async (updatedState) => handleSave({ ...getAllData(), ...updatedState });
  });

  const handleEditCategory = (cat) => { playClickSound(); setModalType('category'); setModalForm({ name: cat.name, originalName: cat.name, color: cat.color, taxable: isCategoryTaxable(categories, cat.name), isEdit: true }); setModalVisible(true); };

  const handleEditSpecialty = (sp) => { playClickSound(); setModalType('specialty'); setModalForm({ name: sp.name, originalName: sp.name, category: sp.category || '', color: sp.color || defaults.categoryColor || '#314e4a', isEdit: true }); setModalVisible(true); };

  // eslint-disable-next-line no-unused-vars
  const handleOpenAddServiceModal = () => {
    playClickSound();
    setModalType('service');
    setModalForm({ name: '', category: selectedCategory || (categories[0]?.name || ''), description: '', durationPrices: [], isEdit: false, isCurrentlyAvailableMassage: false, isCurrentlyAvailableAcupuncture: false, isInsuranceAccepted: false, isCurrentlyAvailableSkinCare: false, isExtractionsIncluded: false, isSafeForPrenatal: false, isArchived: false });
    setModalVisible(true);
  };

  const handleOpenEditServiceModal = (svc) => {
    playClickSound();
    setModalType('service');
    const durationPrices = Array.isArray(svc.durationPrices) ? svc.durationPrices : [];
    setModalForm({ ...svc, originalName: svc.name, description: svc.description || '', durationPrices, isEdit: true, isCurrentlyAvailableMassage: svc.isCurrentlyAvailableMassage || false, isCurrentlyAvailableAcupuncture: svc.isCurrentlyAvailableAcupuncture || false, isInsuranceAccepted: svc.isInsuranceAccepted || false, isCurrentlyAvailableSkinCare: svc.isCurrentlyAvailableSkinCare || false, isExtractionsIncluded: svc.isExtractionsIncluded || false, isSafeForPrenatal: svc.isSafeForPrenatal || false });
    setModalVisible(true);
  };

  const handleEditPractitioner = (prac) => { playClickSound(); setModalType('practitioner'); const offeredServices = Array.isArray(prac.offered_services) ? prac.offered_services : []; const insuranceNetworks = Array.isArray(prac.insuranceNetworks) ? prac.insuranceNetworks : []; setModalForm({ ...prac, originalName: prac.name, offered_services: offeredServices, insuranceNetworks, isEdit: true, bedHeight: prac.bedHeight || 3 }); const initialCollapsedState = {}; offeredServices.forEach(os => { if (os.serviceName) initialCollapsedState[os.serviceName] = true; }); setCollapsedServices(initialCollapsedState); setModalVisible(true); setNewInsuranceNetwork(''); };

  // Overview panel drag handlers (handleDragMouseDown + drag listeners +
  // post-drop save) are owned by useDisplaySettings.



  const launchConfetti = () => { for (let i = 0; i < 50; i++) { const c = document.createElement('div'); c.style.left = `${Math.random() * 100}vw`; c.style.top = `${Math.random() * 100}vh`; c.style.backgroundColor = `hsl(${Math.random() * 360},100%,50%)`; c.style.width = `${Math.random() * 10 + 5}px`; c.style.height = c.style.width; c.style.opacity = '1'; c.style.position = 'fixed'; c.style.zIndex = '9999'; c.style.pointerEvents = 'none'; document.body.appendChild(c); c.animate([{ transform: `translateY(0px) translateX(0px) rotate(0deg)`, opacity: 1 }, { transform: `translateY(${Math.random() * 500 - 250}px) translateX(${Math.random() * 400 - 200}px) rotate(${Math.random() * 720 + 360}deg)`, opacity: 0 }], { duration: 2000 + Math.random() * 3000, easing: 'ease-out', fill: 'forwards' }).onfinish = () => c.remove(); } };
  const handleHeaderClick = () => { playClickSound(); setIsHeaderBouncing(true); setTimeout(() => setIsHeaderBouncing(false), 300); setClickCount(pC => { const nC = pC + 1; if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current); clickTimeoutRef.current = setTimeout(() => { setClickCount(0); setShowEasterEggText(false); }, 500); if (nC === 3) { launchConfetti(); setShowEasterEggText(true); setClickCount(0); if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current); } return nC; }); };
  const handleDownloadData = () => {
    const blob = new Blob([JSON.stringify(getAllData(), null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'modality_data.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenDataFolder = () => {
    if (window.electronAPI) {
      window.electronAPI.openDataFolder();
    }
  };

  // pdfErrorCount, the Chromium PDF error listener, the url-loader
  // effect that resolves viewingDocument → pdfViewerUrl/title/filename,
  // the final-url builder (data: HTML wrap / #view=FitH hash append),
  // and handlePrintPdf / handleDownloadPdf all moved into usePdfViewer.

  // appendAcknowledgement + handleAcknowledge + handleSaveSignature
  // + handleDeleteAcknowledgement all moved into useAppData.

  const logoUrl = process.env.PUBLIC_URL + "/logo.png";
  // viewerRef moved into usePdfViewer.

  // playClickSound / triggerWebAudioClick / getShiftStyles /
  // getServiceInfoFontSizeClass / increaseFontSize / decreaseFontSize are
  // all provided by useDisplaySettings (destructured above).

  const activePractitioners = data.practitioners.filter(p => !p.isArchived);
  const activeServices = data.services.filter(s => !s.isArchived);
  const archivedPractitioners = data.practitioners.filter(p => p.isArchived);
  const archivedServices = data.services.filter(s => s.isArchived);

  const { primaryServicesForCategory, addonServicesForCategory } = React.useMemo(() => {
    if (!selectedCategory) return { primaryServicesForCategory: [], addonServicesForCategory: [] };
    const allServicesForCategory = activeServices
      .filter(s => s.category === selectedCategory)
      .sort((a, b) => a.name.localeCompare(b.name));

    const primary = allServicesForCategory.filter(s => !ADDON_REGEX.test(s.name));
    const addons = allServicesForCategory.filter(s => ADDON_REGEX.test(s.name));

    return { primaryServicesForCategory: primary, addonServicesForCategory: addons };
  }, [selectedCategory, activeServices]);

  const { primaryOfferedServices, addonOfferedServices } = React.useMemo(() => {
    if (!selectedPractitioner?.offered_services) {
      return { primaryOfferedServices: [], addonOfferedServices: [] };
    }
    const sortedServices = [...selectedPractitioner.offered_services].sort((a, b) => (a.serviceName || "").localeCompare(b.serviceName || ""));

    const primary = sortedServices.filter(os => os.serviceName && !ADDON_REGEX.test(os.serviceName));
    const addons = sortedServices.filter(os => os.serviceName && ADDON_REGEX.test(os.serviceName));

    return { primaryOfferedServices: primary, addonOfferedServices: addons };
  }, [selectedPractitioner]);

  // selectedGradientCss / animationClassName / patternClassName are
  // provided by useDisplaySettings (destructured above).

  // ---- minimal History-API routing -------------------------------------
  // Inventory (and any future top-level page) lives at its own URL so it
  // can render full-window without competing with the home view's
  // animations/overlays. We use the History API directly to avoid pulling
  // in react-router for a single extra route.
  const [route, setRoute] = useState(typeof window !== 'undefined' ? window.location.pathname : '/');
  useEffect(() => {
    const onPop = () => setRoute(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const navigate = useCallback((path) => {
    if (typeof window === 'undefined') return;
    window.history.pushState({}, '', path);
    setRoute(path);
  }, []);

  if (route.startsWith('/inventorycheck')) {
    // useAuth installs a window-level click/key blocker while frontdeskAuth
    // is false. Tag the inventory subtree with data-allow-interaction so
    // the splash + login modal stay clickable for users who've never logged
    // into Modality FD; the inventory module enforces its own auth.
    return (
      <div data-allow-interaction>
        <React.Suspense fallback={<div className="min-h-screen bg-[#314e4a] text-white flex items-center justify-center">Loading inventory…</div>}>
          <InventoryApp onExit={() => navigate('/')} />
        </React.Suspense>
      </div>
    );
  }

  if (route.startsWith('/invoices')) {
    // Same isolation as inventory: tag the subtree with data-allow-interaction
    // so the invoice UI stays clickable even before a Modality FD click-blocker
    // would otherwise gate it. The invoice module enforces its own access.
    return (
      <div data-allow-interaction>
        <React.Suspense fallback={<div className="min-h-screen bg-[#1a2e2b] text-white flex items-center justify-center">Loading Invoice Generator…</div>}>
          <InvoiceApp onExit={() => navigate('/')} />
        </React.Suspense>
      </div>
    );
  }

  if (route.startsWith('/coverage')) {
    // Same isolation as inventory/invoices: the coverage module enforces its
    // own auth (practitioner/admin/frontdesk), so practitioners who never
    // logged into Modality FD must be able to reach the splash + login. Tag the
    // subtree with data-allow-interaction so useAuth's window-level click
    // blocker doesn't swallow clicks here.
    return (
      <div data-allow-interaction>
        <React.Suspense fallback={<div className="min-h-screen bg-[#314e4a] text-white flex items-center justify-center">Loading Coverage…</div>}>
          <CoverageApp onExit={() => navigate('/')} />
        </React.Suspense>
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen text-white overflow-hidden"
      style={{
        backgroundColor: mainBgColor,
        transition: 'background-color 600ms ease',
        // Each layer has its own tempo CSS var so the gradient and the
        // pattern overlay can move at independent speeds. CSS keyframes
        // read these vars via animation-duration: var(--xxx-speed).
        '--animation-speed': `${animationSpeed}s`,
        '--pattern-speed': `${patternSpeed}s`,
      }}
    >
      {process.env.REACT_APP_MODALITY_ENV === 'staging' && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-400 text-gray-900 text-center text-xs font-semibold py-1.5 shadow-md pointer-events-none">
          Staging / preview — not production
        </div>
      )}

      {networkToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] flex items-center gap-3 bg-gray-900 text-white text-sm px-5 py-3 rounded-xl shadow-2xl animate-fade-in">
          <span>⚠ {networkToast}</span>
          <button onClick={() => setNetworkToast('')} className="text-gray-400 hover:text-white transition-colors font-bold leading-none">✕</button>
        </div>
      )}
      <BackgroundLayer
        animationClassName={animationClassName}
        patternClassName={patternClassName}
        selectedGradientCss={selectedGradientCss}
        animationOpacity={animationOpacity}
        patternOpacity={patternOpacity}
        logoSrc={SlopeLogoGreen}
        disableBackgroundEffects={disableBackgroundEffects}
      />

      <TopBar
        isMuted={isMuted}
        setIsMuted={setIsMuted}
        jonathanStatus={jonathanStatus}
        supportUnreadCount={supportUnreadCount}
        onOpenSupport={() => {
          // First time staff opens the support drawer is a good
          // user-gesture moment to request notification perms —
          // no earlier prompt, no nagging if they never use it.
          requestNotificationPermission();
          setSupportDrawerOpen(true);
        }}
        onOpenSop={() => setSopModalVisible(true)}
        sopButtonText={sopButtonText}
        sopButtonBgColor={sopButtonBgColor}
        sopButtonTextColor={sopButtonTextColor}
        canSeeInventory={Boolean(adminMode || frontdeskAuth)}
        onOpenInventory={() => navigate('/inventorycheck')}
        canSeeInvoices={Boolean(adminMode || frontdeskAuth)}
        onOpenInvoices={() => navigate('/invoices')}
      />

      <DocumentNotifications
        documents={documents}
        dismissedNotifications={dismissedNotifications}
        setDismissedNotifications={setDismissedNotifications}
        onOpenDocument={(doc) => { setViewingDocument(doc); setSopModalVisible(true); }}
        playClickSound={playClickSound}
      />

      {sopModalVisible && (
        <React.Suspense fallback={<div>Loading...</div>}>
          <SopModal
            sopModalVisible={sopModalVisible}
            setSopModalVisible={setSopModalVisible}
            sopSearchQuery={sopSearchQuery}
            setSopSearchQuery={setSopSearchQuery}
            selectedSopCategory={selectedSopCategory}
            setSelectedSopCategory={setSelectedSopCategory}
            viewingArchived={viewingArchived}
            setViewingArchived={setViewingArchived}
            adminMode={adminMode}
            documentCategories={documentCategories}
            setDocumentCategories={(newCats) => {
              setDocumentCategories(newCats);
              handleSave({ ...getAllData(), documentCategories: newCats });
            }}
            setModalType={setModalType}
            setSopModalForm={setSopModalForm}
            setModalVisible={setModalVisible}
            documents={documents}
            getStatusOutlineClass={getStatusOutlineClass}
            getStatusBadgeClass={getStatusBadgeClass}
            getStatusEmoji={getStatusEmoji}
            setViewingDocument={setViewingDocument}
            setActiveViewerTab={setActiveViewerTab}
            setConfirmMessage={setConfirmMessage}
            setConfirmAction={setConfirmAction}
            setShowConfirmModal={setShowConfirmModal}
            handleDeleteDocument={handleDeleteDocument}
          />
        </React.Suspense>
      )}

      <LiveChatRail
        jonathanStatus={jonathanStatus}
        fdLiveChatSession={fdLiveChatSession}
        liveChatRailOpen={liveChatRailOpen}
        setLiveChatRailOpen={setLiveChatRailOpen}
        fdLiveChatHasUnread={fdLiveChatHasUnread}
        fdLiveChatShared={fdLiveChatShared}
        supportSelectedEmployeeId={supportSelectedEmployeeId}
        fdSupportEmployeesList={fdSupportEmployeesList}
        handleSelectSupportEmployee={handleSelectSupportEmployee}
        documents={documents}
        onOpenSopDocument={(doc) => {
          setViewingDocument(doc);
          setSopModalVisible(true);
        }}
      />


      <SupportDrawer
        open={supportDrawerOpen}
        onClose={() => setSupportDrawerOpen(false)}
        supportStatus={jonathanStatus}
        categories={supportCategories}
        documents={documents}
        employees={supportEmployees}
        selectedEmployeeId={supportSelectedEmployeeId}
        onSelectEmployee={handleSelectSupportEmployee}
        ticketCount={Array.isArray(supportMyTickets) ? supportMyTickets.length : 0}
        unreadCount={supportUnreadCount}
        onOpenTickets={() => {
          setSupportDrawerOpen(false);
          setTicketsDrawerOpen(true);
        }}
        onOpenSopDocument={(doc) => {
          setViewingDocument(doc);
          setSopModalVisible(true);
        }}
        onLiveChatOpenChange={setLiveChatOpen}
        liveChatShared={fdLiveChatShared}
      />

      <TicketsDrawer
        open={ticketsDrawerOpen}
        onClose={() => setTicketsDrawerOpen(false)}
        tickets={supportMyTickets}
        documents={documents}
        unreadSeenAt={supportSeenAt}
        ticketSeenMap={supportTicketSeenMap}
        onMarkTicketSeen={handleMarkTicketSeen}
        onMarkAllSeen={handleMarkSupportSeen}
        staffNameHint={(supportEmployees.find((e) => e.id === supportSelectedEmployeeId) || {}).name || ''}
        onOpenSopDocument={(doc) => {
          setViewingDocument(doc);
          setSopModalVisible(true);
        }}
      />

      <TicketUpdateBanner
        supportUnreadCount={supportUnreadCount}
        supportDrawerOpen={supportDrawerOpen}
        ticketsDrawerOpen={ticketsDrawerOpen}
        onOpenTickets={() => setTicketsDrawerOpen(true)}
      />

      <div className="relative z-10 p-6 max-w-6xl mx-auto">
        <div className="text-center mb-4">
          <h1 className={`inline-block font-bold drop-shadow-lg tracking-wide transition-all duration-300 ease-in-out hover:text-shadow-glow cursor-pointer select-none ${isHeaderBouncing ? 'animate-bounce-short' : ''}`} onClick={handleHeaderClick} style={{ color: titleConfig.color, fontSize: titleConfig.fontSize, userSelect: 'none' }}>{titleConfig.text}</h1>
        </div>
        <style>{`
          .hover\\:text-shadow-glow:hover{text-shadow:0 0 8px rgba(255,255,255,0.8), 0 0 15px rgba(255,255,255,0.5);}
          .select-none{user-select:none;}
          @keyframes bounce-short{0%,100%{transform:translateY(0);}50%{transform:translateY(-10px);}}
          .animate-bounce-short{animation:bounce-short 0.3s ease-in-out;}
          @keyframes gold-glow {
            0% { box-shadow: 0 0 5px #FFD700; }
            50% { box-shadow: 0 0 20px #FFD700; }
            100% { box-shadow: 0 0 5px #FFD700; }
          }
          .gold-glow-border {
            animation: gold-glow 2s infinite alternate;
            border-color: #FFD700;
          }
        `}</style>

        <MainPanel
          searchWrapperRef={searchWrapperRef}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchResults={searchResults}
          isSearchFocused={isSearchFocused}
          setIsSearchFocused={setIsSearchFocused}
          handleSearchResultClick={handleSearchResultClick}
          dataLoading={dataLoading}
          categories={categories}
          specialties={specialties}
          activeServices={activeServices}
          activePractitioners={activePractitioners}
          defaults={defaults}
          playClickSound={playClickSound}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          selectedService={selectedService}
          setSelectedService={setSelectedService}
          selectedPractitioner={selectedPractitioner}
          setSelectedPractitioner={setSelectedPractitioner}
          activePracTab={activePracTab}
          setActivePracTab={setActivePracTab}
          setMainBgColor={setMainBgColor}
          primaryServicesForCategory={primaryServicesForCategory}
          addonServicesForCategory={addonServicesForCategory}
          includeSalesTax={includeSalesTax}
          setIncludeSalesTax={setIncludeSalesTax}
          appSalesTaxRate={appSalesTaxRate}
          serviceInfoFontSizeLevel={serviceInfoFontSizeLevel}
          increaseFontSize={increaseFontSize}
          decreaseFontSize={decreaseFontSize}
          getServiceInfoFontSizeClass={getServiceInfoFontSizeClass}
          adminMode={adminMode}
          handleOpenEditServiceModal={handleOpenEditServiceModal}
          primaryOfferedServices={primaryOfferedServices}
          addonOfferedServices={addonOfferedServices}
          handleEditPractitioner={handleEditPractitioner}
          DAYS={DAYS}
          getShiftStyles={getShiftStyles}
        />
      </div>



      <React.Suspense fallback={<div>Loading...</div>}>
        <AdminPanel
          adminMode={adminMode}
          showAnimationControls={showAnimationControls}
          setShowAnimationControls={setShowAnimationControls}
          handleDownloadData={handleDownloadData}
          handleOpenDataFolder={handleOpenDataFolder}
          gradientPreset={gradientPreset}
          setGradientPreset={setGradientPreset}
          gradientPresets={gradientPresets}
          animationOpacity={animationOpacity}
          setAnimationOpacity={setAnimationOpacity}
          animationSpeed={animationSpeed}
          setAnimationSpeed={setAnimationSpeed}
          customGradientColors={customGradientColors}
          setCustomGradientColors={setCustomGradientColors}
          gradientAngle={gradientAngle}
          setGradientAngle={setGradientAngle}
          animationStyle={animationStyle}
          setAnimationStyle={setAnimationStyle}
          patternStyle={patternStyle}
          setPatternStyle={setPatternStyle}
          patternSpeed={patternSpeed}
          setPatternSpeed={setPatternSpeed}
          patternOpacity={patternOpacity}
          setPatternOpacity={setPatternOpacity}
          disableBackgroundEffects={disableBackgroundEffects}
          setDisableBackgroundEffects={setDisableBackgroundEffects}
          animationStyles={ANIMATION_STYLES}
          patternStyles={PATTERN_STYLES}
          handleSaveAllSettings={handleSaveAllSettings}
          appSalesTaxRate={appSalesTaxRate}
          setAppSalesTaxRate={setAppSalesTaxRate}
          sopButtonText={sopButtonText}
          setSopButtonText={setSopButtonText}
          sopButtonLink={sopButtonLink}
          setSopButtonLink={setSopButtonLink}
          sopButtonBgColor={sopButtonBgColor}
          setSopButtonBgColor={setSopButtonBgColor}
          sopButtonTextColor={sopButtonTextColor}
          setSopButtonTextColor={setSopButtonTextColor}
          amShiftBgColor={amShiftBgColor}
          setAmShiftBgColor={setAmShiftBgColor}
          pmShiftBgColor={pmShiftBgColor}
          setPmShiftBgColor={setPmShiftBgColor}
          offShiftBgColor={offShiftBgColor}
          setOffShiftBgColor={setOffShiftBgColor}
          doubleShiftBgColor={doubleShiftBgColor}
          setDoubleShiftBgColor={setDoubleShiftBgColor}
          amShiftTextColor={amShiftTextColor}
          setAmShiftTextColor={setAmShiftTextColor}
          pmShiftTextColor={pmShiftTextColor}
          setPmShiftTextColor={setPmShiftTextColor}
          offShiftTextColor={offShiftTextColor}
          setOffShiftTextColor={setOffShiftTextColor}
          doubleShiftTextColor={doubleShiftTextColor}
          setDoubleShiftTextColor={setDoubleShiftTextColor}
          handleLogout={handleLogout}
          overviewPosition={overviewPosition}
          overviewRef={overviewRef}
          handleDragMouseDown={handleDragMouseDown}
          onOpenCoverage={() => navigate('/coverage')}
          isOverviewCollapsed={isOverviewCollapsed}
          setIsOverviewCollapsed={setIsOverviewCollapsed}
          categories={categories}
          handleEditCategory={handleEditCategory}
          specialties={specialties}
          handleEditSpecialty={handleEditSpecialty}
          activeServices={activeServices}
          handleOpenEditServiceModal={handleOpenEditServiceModal}
          activePractitioners={activePractitioners}
          handleEditPractitioner={handleEditPractitioner}
          archivedServices={archivedServices}
          handleRestore={handleRestore}
          archivedPractitioners={archivedPractitioners}
          saveStatus={saveStatus}
          playClickSound={playClickSound}
          defaults={defaults}
          setModalType={setModalType}
          setModalForm={setModalForm}
          setModalVisible={setModalVisible}
          setSopModalVisible={setSopModalVisible}
          setSopModalForm={setSopModalForm}
          titleConfig={titleConfig}
          linkTreeConfig={linkTreeConfig}
          handleSetAdminPassword={handleSetAdminPassword}
          supportCategories={supportCategories}
          setSupportCategories={setSupportCategories}
          supportEmployees={supportEmployees}
          setSupportEmployees={setSupportEmployees}
          jonathanStatus={jonathanStatus}
          jonathanAvailableUntil={jonathanAvailableUntil}
          onSetJonathanStatus={handleSetJonathanStatus}
          documents={documents}
          onOpenSopDocument={(doc) => {
            setViewingDocument(doc);
            setSopModalVisible(true);
          }}
        />
      </React.Suspense>

      <LinkTreeButton
        linkTreeConfig={linkTreeConfig}
        isLinkTreeVisible={isLinkTreeVisible}
        setIsLinkTreeVisible={setIsLinkTreeVisible}
        logoUrl={logoUrl}
        playClickSound={playClickSound}
        linkTreeRef={linkTreeRef}
      />

      <LoginGates
        frontdeskAuth={frontdeskAuth}
        authHydrated={authHydrated}
        fdPassword={fdPassword}
        setFdPassword={setFdPassword}
        fdLoginError={fdLoginError}
        setFdLoginError={setFdLoginError}
        handleFrontdeskLogin={handleFrontdeskLogin}
        adminMode={adminMode}
        loginVisible={loginVisible}
        setLoginVisible={setLoginVisible}
        password={password}
        setPassword={setPassword}
        adminLoginError={adminLoginError}
        setAdminLoginError={setAdminLoginError}
        handleLogin={handleLogin}
        playClickSound={playClickSound}
        inputRef={inputRef}
        loginTimeoutRef={loginTimeoutRef}
      />

      

      <React.Suspense fallback={<div>Loading...</div>}>
        <Modals
          modalVisible={modalVisible}
          handleCloseModal={handleCloseModal}
          modalType={modalType}
          modalForm={modalForm}
          setModalForm={setModalForm}
          defaults={defaults}
          handleDelete={handleDelete}
          handleArchive={handleArchive}
          playClickSound={playClickSound}
          setConfirmMessage={setConfirmMessage}
          setConfirmAction={setConfirmAction}
          setShowConfirmModal={setShowConfirmModal}
          sopModalForm={sopModalForm}
          setSopModalForm={setSopModalForm}
          documentCategories={documentCategories}
          setDocumentCategories={setDocumentCategories}
          documents={documents}
          setDocuments={setDocuments}
          categories={categories}
          specialties={specialties}
          supportCategories={supportCategories}
          newInsuranceNetwork={newInsuranceNetwork}
          setNewInsuranceNetwork={setNewInsuranceNetwork}
          SHIFTS={SHIFTS}
          DAYS={DAYS}
          ADDON_REGEX={ADDON_REGEX}
          ADDON_CLEANUP_REGEX={ADDON_CLEANUP_REGEX}
          activeServices={activeServices}
          collapsedServices={collapsedServices}
          toggleServiceCollapse={toggleServiceCollapse}
          handleModalSave={handleModalSave}
        />
      </React.Suspense>


      <ConfirmModal
        show={showConfirmModal}
        message={confirmMessage}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={confirmAction}
        playClickSound={playClickSound}
      />
      {showEasterEggText && (<div className="fixed bottom-4 right-20 text-white text-sm bg-gray-800 bg-opacity-75 px-3 py-2 rounded-lg z-50 animate-fade-in">Created by John</div>)}
      <DocumentViewer
        viewingDocument={viewingDocument}
        onClose={closeViewer}
        viewerRef={viewerRef}
        pdfFinalUrl={pdfFinalUrl}
        pdfZoomLevel={pdfZoomLevel}
        setPdfZoomLevel={setPdfZoomLevel}
        pdfErrorCount={pdfErrorCount}
        isPdfFilterEnabled={isPdfFilterEnabled}
        setIsPdfFilterEnabled={setIsPdfFilterEnabled}
        isSigning={isSigning}
        setIsSigning={setIsSigning}
        ackName={ackName}
        setAckName={setAckName}
        hideDescription={hideDescription}
        setHideDescription={setHideDescription}
        hideDocument={hideDocument}
        setHideDocument={setHideDocument}
        hideVideo={hideVideo}
        setHideVideo={setHideVideo}
        descPercent={descPercent}
        startDrag={startDrag}
        handlePrintPdf={handlePrintPdf}
        handleDownloadPdf={handleDownloadPdf}
        handleSaveSignature={handleSaveSignature}
        handleDeleteAcknowledgement={handleDeleteAcknowledgement}
        adminMode={adminMode}
      />
    </div>
  );
}
